/**
 * SalarieDetail — Fiche salarié avec 6 tabs (parité BE-TAYE).
 * Perso · Pro · Contrats · Rémunération · Bulletins · Absences
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  ArrowLeft, Save, Trash2, UserCircle, Briefcase, FileText,
  Loader2, DollarSign, ReceiptText, Calendar, Upload, Download, Sparkles, Plus,
  Wallet, Sun, CircleDollarSign, Check, X, CheckCheck, Clock,
  Folder, Stethoscope, GraduationCap, MessageSquare, Gavel, Heart, Eye,
} from "lucide-react";
import { PayslipPdfDialog } from "@/components/hr/PayslipPdfDialog";
import { toast } from "sonner";
import { DocumentsTab, MedicalTab, TrainingTab, InterviewTab } from "@/components/SalarieTabsExtras";
import AIContractImportWizard from "@/components/hr/AIContractImportWizard";
import GarnishmentsTab from "@/components/hr/GarnishmentsTab";
import MutuelleTab from "@/components/hr/MutuelleTab";

const CONTRACT_TYPES = [
  { value: "cdi", label: "CDI" }, { value: "cdd", label: "CDD" },
  { value: "alternance", label: "Alternance" }, { value: "stage", label: "Stage" },
  { value: "interim", label: "Intérim" },
];
const STATUSES = [
  { value: "active", label: "Actif" }, { value: "on_leave", label: "En congé" },
  { value: "terminated", label: "Sorti" },
];
const ABS_TYPE_LABEL = {
  cp: "CP", rtt: "RTT", maladie: "Arrêt maladie",
  at: "Accident travail", maternite: "Maternité", paternite: "Paternité",
  parental: "Congé parental", famille: "Événement famille",
  formation: "Formation", teletravail: "Télétravail",
  sans_solde: "Sans solde", autre: "Autre",
};
const ABS_STATUS = {
  pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "À valider" },
  approved: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Validée" },
  rejected: { cls: "bg-red-100 text-red-700 border-red-200", label: "Refusée" },
};

const fmtEUR = (v) => v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPeriodFR = (p) => {
  if (!p || p.length !== 7) return p || "—";
  const [y, m] = p.split("-");
  const months = ["janv.", "févr.", "mars", "avr.", "mai", "juin",
                   "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

/* -------- Solde Congés Widget -------- */
function LeaveBalanceCard({ employeeId, canWrite }) {
  const [bal, setBal] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [cfg, setCfg] = useState({ cp_carryover: 0, cp_acquired_override: "" });
  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/leave-balance`); setBal(r.data); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);
  const openEdit = async () => {
    try {
      const r = await api.get(`/hr/employees/${employeeId}`);
      const lc = r.data.employee?.leave_config || {};
      setCfg({
        cp_carryover: lc.cp_carryover ?? 0,
        cp_acquired_override: lc.cp_acquired_override ?? "",
      });
      setEditOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    try {
      const body = {
        leave_config: {
          cp_carryover: parseFloat(cfg.cp_carryover) || 0,
          cp_acquired_override: cfg.cp_acquired_override === "" ? null : parseFloat(cfg.cp_acquired_override),
        },
      };
      await api.patch(`/hr/employees/${employeeId}`, body);
      toast.success("Configuration congés mise à jour");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!bal) return null;
  const pct = bal.cp.acquired > 0 ? Math.min(100, (bal.cp.taken / bal.cp.acquired) * 100) : 0;
  const remaining = Math.max(0, bal.cp.balance);
  return (
    <>
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50/40 to-transparent" data-testid="leave-balance-card">
        <CardHeader className="pb-2 flex flex-row items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
            <Sun className="w-4 h-4" />
          </div>
          <CardTitle className="text-base">Solde de congés</CardTitle>
          <Badge variant="outline" className="ml-auto text-[10px] font-mono">
            période {bal.reference_period.start.slice(0, 4)}/{bal.reference_period.end.slice(0, 4)}
          </Badge>
          {canWrite && (
            <Button size="sm" variant="ghost" onClick={openEdit}
              className="h-7 text-xs" data-testid="edit-leave-config-btn">
              Configurer
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Acquis</p>
              <p className="text-2xl font-bold text-slate-900 font-mono">{bal.cp.acquired}<span className="text-sm text-slate-400 ml-0.5">j</span></p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Pris</p>
              <p className="text-2xl font-bold text-amber-600 font-mono">{bal.cp.taken}<span className="text-sm text-amber-400 ml-0.5">j</span></p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Solde</p>
              <p className={`text-2xl font-bold font-mono ${remaining < 5 ? "text-red-600" : "text-emerald-600"}`}>
                {bal.cp.balance}<span className="text-sm text-emerald-400 ml-0.5">j</span>
              </p>
            </div>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              {bal.reference_period.months_worked} mois travaillés
              {bal.cp.pending > 0 && <span className="text-amber-600 font-medium"> · + {bal.cp.pending} j en attente</span>}
              {bal.cp.carryover > 0 && <span className="text-blue-700 font-medium"> · report {bal.cp.carryover} j</span>}
            </span>
            <span className="text-[10px]">Règle FR 2,5 j/mois · plafond 30 j</span>
          </div>
          <div className="pt-3 border-t grid grid-cols-3 gap-3 text-center">
            <div><p className="text-[10px] text-slate-400 uppercase">RTT {bal.current_year.year}</p>
              <p className="font-mono font-semibold text-slate-800">{bal.current_year.rtt_taken} j</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase">Maladie {bal.current_year.year}</p>
              <p className="font-mono font-semibold text-slate-800">{bal.current_year.maladie_taken} j</p></div>
            <div><p className="text-[10px] text-slate-400 uppercase">Autres {bal.current_year.year}</p>
              <p className="font-mono font-semibold text-slate-800">{bal.current_year.other_taken} j</p></div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="leave-config-dialog">
          <DialogHeader><DialogTitle>Configuration des congés</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Jours reportés de la période précédente</Label>
              <Input type="number" step="0.5" value={cfg.cp_carryover}
                onChange={(e) => setCfg(p => ({ ...p, cp_carryover: e.target.value }))}
                data-testid="leave-carryover-input" />
              <p className="text-xs text-slate-400 mt-1">S'ajoute au droit acquis sur la période courante.</p>
            </div>
            <div><Label>Override manuel du droit acquis (laisser vide pour règle FR)</Label>
              <Input type="number" step="0.5" value={cfg.cp_acquired_override}
                onChange={(e) => setCfg(p => ({ ...p, cp_acquired_override: e.target.value }))}
                placeholder="Auto : 2,5 j × mois travaillés (max 30)"
                data-testid="leave-override-input" />
              <p className="text-xs text-slate-400 mt-1">Utile pour un forfait négocié ou une reprise d'ancienneté.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-leave-config-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------- Rémunération Tab -------- */
function RemunerationTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    period: new Date().toISOString().slice(0, 7),
    bonus_amount: 0, meal_vouchers_days: 0, mutuelle_amount: 0,
    transport_amount: 0, overtime_hours: 0, other_items: [], notes: "",
  });

  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/payroll-variables`);
      setRows(r.data.variables || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/hr/employees/${employeeId}/payroll-variables`, form);
      toast.success("Variables enregistrées");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    try { await api.delete(`/hr/payroll-variables/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Nouvelle période</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label className="text-xs">Période (YYYY-MM)</Label>
              <Input value={form.period} onChange={(e) => setForm(p => ({ ...p, period: e.target.value }))}
                placeholder="2026-02" className="font-mono" data-testid="payroll-period" />
            </div>
            <div><Label className="text-xs">Prime (€)</Label>
              <Input type="number" step="0.01" value={form.bonus_amount}
                onChange={(e) => setForm(p => ({ ...p, bonus_amount: parseFloat(e.target.value) || 0 }))}
                data-testid="payroll-bonus" />
            </div>
            <div><Label className="text-xs">Tickets resto (jours)</Label>
              <Input type="number" value={form.meal_vouchers_days}
                onChange={(e) => setForm(p => ({ ...p, meal_vouchers_days: parseInt(e.target.value) || 0 }))} />
            </div>
            <div><Label className="text-xs">Mutuelle (€)</Label>
              <Input type="number" step="0.01" value={form.mutuelle_amount}
                onChange={(e) => setForm(p => ({ ...p, mutuelle_amount: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div><Label className="text-xs">Transport (€)</Label>
              <Input type="number" step="0.01" value={form.transport_amount}
                onChange={(e) => setForm(p => ({ ...p, transport_amount: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div><Label className="text-xs">Heures supp.</Label>
              <Input type="number" step="0.5" value={form.overtime_hours}
                onChange={(e) => setForm(p => ({ ...p, overtime_hours: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
          <div><Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} />
          </div>
          {canWrite && (
            <div className="flex justify-end">
              <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-payroll-btn">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historique</CardTitle></CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 text-center">Aucune variable enregistrée.</p>
          ) : (
            <div className="divide-y">
              {rows.map((v, i) => (
                <div key={v.id} className="p-3 flex items-center gap-4 hover:bg-slate-50" data-testid={`payroll-row-${i}`}>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-mono">
                    {fmtPeriodFR(v.period)}
                  </Badge>
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div><span className="text-slate-400 text-xs">Prime</span><div className="font-mono">{fmtEUR(v.bonus_amount)}</div></div>
                    <div><span className="text-slate-400 text-xs">TR</span><div className="font-mono">{v.meal_vouchers_days || 0}j</div></div>
                    <div><span className="text-slate-400 text-xs">Mutuelle</span><div className="font-mono">{fmtEUR(v.mutuelle_amount)}</div></div>
                    <div><span className="text-slate-400 text-xs">Transport</span><div className="font-mono">{fmtEUR(v.transport_amount)}</div></div>
                    <div><span className="text-slate-400 text-xs">H. supp.</span><div className="font-mono">{v.overtime_hours || 0}h</div></div>
                  </div>
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={() => remove(v.id)} className="text-red-600"
                      data-testid={`del-payroll-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* -------- Bulletins Tab -------- */
function BulletinsTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [period, setPeriod] = useState("");  // Vide = auto-détection IA
  const [viewing, setViewing] = useState(null);
  const closeViewer = useCallback(() => setViewing(null), []);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [extractAi, setExtractAi] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/payslips`);
      setRows(r.data.payslips || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const list = files;
    if (!list.length) { toast.error("Choisissez au moins un PDF"); return; }
    if (period && period.length !== 7) { toast.error("Format période : YYYY-MM"); return; }
    setUploading(true);
    let ok = 0;
    for (const [i, f] of list.entries()) {
      setProgress({ done: i, total: list.length });
      const fd = new FormData();
      fd.append("file", f);
      if (period && list.length === 1) fd.append("period", period);
      fd.append("extract_ai", extractAi ? "true" : "false");
      try {
        await api.post(`/hr/employees/${employeeId}/payslips/upload`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { toast.error(`${f.name} : ${apiError(e)}`); }
    }
    toast.success(`${ok}/${list.length} bulletin(s) importé(s)${extractAi ? " — périodes et montants extraits par l'IA" : ""}`);
    setUploading(false); setProgress(null);
    setUploadDialog(false); setFiles([]); setPeriod("");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bulletin ?")) return;
    try { await api.delete(`/hr/payslips/${id}`); toast.success("Bulletin supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const download = async (id, name) => {
    try {
      const r = await api.get(`/hr/payslips/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = name || "bulletin.pdf"; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PayslipPdfDialog payslip={viewing} onClose={closeViewer} />
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Bulletins de paie</CardTitle>
          {canWrite && (
            <Button size="sm" onClick={() => setUploadDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-payslip-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importer des bulletins
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-8 text-center">
              <ReceiptText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Aucun bulletin enregistré.</p>
              <p className="text-xs text-slate-400 mt-1">
                <Sparkles className="w-3 h-3 inline text-purple-500" /> L'extraction IA remplit automatiquement les montants
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((p, i) => (
                <div key={p.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`payslip-row-${i}`}>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-mono">
                    {fmtPeriodFR(p.period)}
                  </Badge>
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-400 text-xs">Brut</span><div className="font-mono font-semibold">{fmtEUR(p.gross_salary)}</div></div>
                    <div><span className="text-slate-400 text-xs">Salaire net</span><div className="font-mono font-semibold" data-testid={`payslip-net-${i}`}>{fmtEUR(p.net_salary)}</div>
                      {Number(p.advances_amount) > 0 && <div className="text-[10px] text-amber-700">dont acompte {fmtEUR(p.advances_amount)} · versé fin de mois {fmtEUR(p.net_paid)}</div>}
                      {p.advance_check?.status === "gap" && (
                        <div className="mt-0.5 text-[10px] font-medium text-red-700" data-testid={`payslip-advance-gap-${i}`}
                             title="Comparaison avec les acomptes validés/payés de l'onglet Acomptes pour ce mois">
                          ⚠ Acompte bulletin {fmtEUR(p.advance_check.on_payslip)} ≠ enregistré {fmtEUR(p.advance_check.recorded)}
                        </div>
                      )}
                      {p.advance_check?.status === "ok" && (
                        <div className="mt-0.5 text-[10px] text-emerald-700" data-testid={`payslip-advance-ok-${i}`}>✓ Acompte conforme à l'onglet Acomptes</div>
                      )}</div>
                    <div><span className="text-slate-400 text-xs">Net après IR</span><div className="font-mono">{fmtEUR(p.net_after_tax)}</div></div>
                    <div><span className="text-slate-400 text-xs">Cotisations</span><div className="font-mono">{fmtEUR(p.total_cotisations)}</div></div>
                  </div>
                  <Badge variant="outline" className={
                    p.extraction_status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" :
                    p.extraction_status === "manual" ? "bg-slate-100 text-slate-600 border-slate-200 text-[10px]" :
                    "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                  }>
                    {p.extraction_status === "ok" ? "IA ✓" : p.extraction_status === "manual" ? "Manuel" : "IA échec"}
                  </Badge>
                  {p.file_asset_id && (
                    <Button variant="ghost" size="sm" onClick={() => setViewing(p)} title="Consulter le PDF"
                      className="text-blue-600" data-testid={`view-payslip-${i}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {p.file_asset_id && (
                    <Button variant="ghost" size="sm" title="Télécharger" onClick={() => download(p.id, p.original_filename)}
                      data-testid={`download-payslip-${i}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-red-600"
                      data-testid={`del-payslip-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent data-testid="upload-payslip-dialog">
          <DialogHeader><DialogTitle>Importer des bulletins</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {files.length <= 1 && <div><Label>Période (YYYY-MM) <span className="text-xs text-slate-400 font-normal">— optionnel, auto-détecté par l'IA</span></Label>
              <Input value={period} onChange={(e) => setPeriod(e.target.value)} className="font-mono" placeholder="Laisser vide pour auto-détection IA" />
            </div>}
            <div><Label>Fichiers PDF <span className="text-xs text-slate-400 font-normal">— sélection multiple possible</span></Label>
              <Input type="file" multiple accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="payslip-file-input" />
              {files.length > 1 && <p className="text-xs text-slate-500 mt-1" data-testid="payslip-files-count">{files.length} bulletins sélectionnés — la période de chacun sera détectée par l'IA</p>}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input id="payslip-extract-ai" name="payslip-extract-ai"
                type="checkbox" checked={extractAi} onChange={(e) => setExtractAi(e.target.checked)} />
              <Sparkles className="w-3.5 h-3.5 text-purple-500" />
              Extraire automatiquement montants + période (IA)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>Annuler</Button>
            <Button onClick={upload} disabled={uploading} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-upload-payslip">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {uploading ? (progress ? `Import ${progress.done + 1}/${progress.total}…` : "Import...") : "Importer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------- Absences Tab -------- */
function AbsencesTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ type: "cp", start_date: "", end_date: "", reason: "" });

  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/absences`);
      setRows(r.data.absences || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Dates requises"); return; }
    try { await api.post(`/hr/employees/${employeeId}/absences`, form);
      toast.success("Absence créée"); setAddOpen(false);
      setForm({ type: "cp", start_date: "", end_date: "", reason: "" });
      load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (id) => {
    try { await api.post(`/hr/absences/${id}/approve`); load(); toast.success("Validée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/hr/absences/${id}/reject`); load(); toast.success("Refusée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette absence ?")) return;
    try { await api.delete(`/hr/absences/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <LeaveBalanceCard employeeId={employeeId} canWrite={canWrite} />
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Absences & congés</CardTitle>
          {canWrite && (
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-absence-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle absence
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 text-center">Aucune absence enregistrée.</p>
          ) : (
            <div className="divide-y">
              {rows.map((a, i) => {
                const st = ABS_STATUS[a.status] || ABS_STATUS.pending;
                return (
                  <div key={a.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`emp-absence-${i}`}>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                      {ABS_TYPE_LABEL[a.type]}
                    </Badge>
                    <div className="flex-1 text-sm font-mono">
                      {new Date(a.start_date).toLocaleDateString("fr-FR")} → {new Date(a.end_date).toLocaleDateString("fr-FR")}
                    </div>
                    <div className="text-sm font-mono font-semibold">{a.days_count?.toFixed(1) || "—"} j</div>
                    <Badge variant="outline" className={`text-xs ${st.cls}`}>{st.label}</Badge>
                    {canWrite && a.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => approve(a.id)} className="text-emerald-600 h-7"
                          data-testid={`emp-approve-${i}`}>✓</Button>
                        <Button size="sm" variant="ghost" onClick={() => reject(a.id)} className="text-red-600 h-7">✗</Button>
                      </>
                    )}
                    {canWrite && (
                      <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="text-slate-500 h-7">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle absence</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ABS_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Début</Label>
                <DatePickerFR value={form.start_date} onChange={(v) => setForm(p => ({ ...p, start_date: v }))} />
              </div>
              <div><Label>Fin</Label>
                <DatePickerFR value={form.end_date} onChange={(v) => setForm(p => ({ ...p, end_date: v }))} />
              </div>
            </div>
            <div><Label>Motif</Label>
              <Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={create} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-emp-absence-btn">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------- Acomptes Tab -------- */
const ADV_STATUS = {
  pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "À valider", icon: Clock },
  approved: { cls: "bg-blue-100 text-blue-700 border-blue-200", label: "Validé", icon: Check },
  paid:     { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Payé", icon: CheckCheck },
  rejected: { cls: "bg-red-100 text-red-700 border-red-200", label: "Refusé", icon: X },
};

function AcomptesTab({ employeeId, canWrite }) {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    amount: "", requested_date: new Date().toISOString().slice(0, 10),
    deduction_period: "", reason: "",
  });

  const load = useCallback(async () => {
    try { const r = await api.get(`/hr/employees/${employeeId}/advances`); setRows(r.data.advances || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.amount) { toast.error("Montant requis"); return; }
    try {
      await api.post(`/hr/employees/${employeeId}/advances`,
        { ...form, amount: parseFloat(form.amount) });
      toast.success("Demande créée");
      setAddOpen(false);
      setForm({ amount: "", requested_date: new Date().toISOString().slice(0, 10),
                 deduction_period: "", reason: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (id) => {
    try { await api.post(`/hr/advances/${id}/approve`); load(); toast.success("Validé"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/hr/advances/${id}/reject`); load(); toast.success("Refusé"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pay = async (id) => {
    if (!window.confirm("Marquer comme payé et injecter la retenue en paie ?")) return;
    try { await api.post(`/hr/advances/${id}/pay`); load(); toast.success("Payé + retenue injectée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try { await api.delete(`/hr/advances/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Acomptes</CardTitle>
          {canWrite && (
            <Button size="sm" onClick={() => setAddOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-advance-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle demande
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6 text-center">
              <Wallet className="w-8 h-8 mx-auto text-slate-300 mb-1" />
              <p className="text-sm text-slate-500">Aucun acompte enregistré.</p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((a, i) => {
                const st = ADV_STATUS[a.status] || ADV_STATUS.pending;
                const Icon = st.icon;
                return (
                  <div key={a.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`emp-advance-${i}`}>
                    <div className="w-24 font-mono font-semibold">
                      {Number(a.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="text-slate-700">{a.reason || "—"}</div>
                      <div className="text-xs text-slate-400 font-mono">
                        Demande {a.requested_date && new Date(a.requested_date).toLocaleDateString("fr-FR")}
                        {a.deduction_period && ` · retenue ${fmtPeriodFR(a.deduction_period)}`}
                      </div>
                    </div>
                    <Badge variant="outline" className={st.cls}>
                      <Icon className="w-3 h-3 mr-1" />{st.label}
                    </Badge>
                    {canWrite && a.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => approve(a.id)} className="text-emerald-600 h-7">✓</Button>
                        <Button size="sm" variant="ghost" onClick={() => reject(a.id)} className="text-red-600 h-7">✗</Button>
                      </>
                    )}
                    {canWrite && a.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => pay(a.id)}
                        className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700"
                        data-testid={`emp-pay-adv-${i}`}>
                        <CircleDollarSign className="w-3 h-3 mr-1" />Payer
                      </Button>
                    )}
                    {canWrite && (
                      <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="text-slate-500 h-7">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle demande d'acompte</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Montant (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.amount}
                  onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                  data-testid="emp-adv-amount" />
              </div>
              <div><Label>Date demande</Label>
                <DatePickerFR value={form.requested_date}
                  onChange={(v) => setForm(p => ({ ...p, requested_date: v }))} />
              </div>
            </div>
            <div><Label>Retenue sur bulletin (YYYY-MM)</Label>
              <Input value={form.deduction_period}
                onChange={(e) => setForm(p => ({ ...p, deduction_period: e.target.value }))}
                placeholder="Auto : mois suivant" className="font-mono" />
            </div>
            <div><Label>Motif</Label>
              <Input value={form.reason} onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={create} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-emp-advance-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------- Main component ---------- */
export default function SalarieDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [emp, setEmp] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [aiEnrichOpen, setAiEnrichOpen] = useState(false);

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const r = await api.get(`/hr/employees/${id}`);
      setEmp(r.data.employee); setContracts(r.data.contracts || []);
      setForm(r.data.employee);
    } catch (e) { toast.error(apiError(e)); }
    finally { if (initial) setLoading(false); }
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [id]);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updNested = (parent, k, v) => setForm(p => ({ ...p, [parent]: { ...(p[parent] || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/hr/employees/${id}`, form); toast.success("Salarié mis à jour"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const removeEmp = async () => {
    if (!window.confirm(`Supprimer ${emp.first_name} ${emp.last_name} ?`)) return;
    try { await api.delete(`/hr/employees/${id}`); toast.success("Salarié supprimé"); navigate("/app/hr/salaries"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addContract = async () => {
    const payload = { contract_type: "cdi", start_date: new Date().toISOString().slice(0, 10) };
    try { await api.post(`/hr/employees/${id}/contracts`, payload); toast.success("Contrat créé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const updContract = async (cid, patch) => {
    try { await api.patch(`/hr/contracts/${cid}`, patch); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeContract = async (cid) => {
    if (!window.confirm("Supprimer ce contrat ?")) return;
    try { await api.delete(`/hr/contracts/${cid}`); toast.success("Contrat supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!emp) return <div className="p-6 text-slate-500">Salarié introuvable.</div>;

  return (
    <div className="space-y-6" data-testid="salarie-detail-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/hr/salaries")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              {emp.first_name} {emp.last_name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {emp.job_title && <Badge variant="outline">{emp.job_title}</Badge>}
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {STATUSES.find(s => s.value === emp.status)?.label || "—"}
              </Badge>
            </div>
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline"
              onClick={() => setAiEnrichOpen(true)}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-enrich-from-contract-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Enrichir depuis contrat
            </Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-emp-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Enregistrer"}
            </Button>
            <Button variant="outline" onClick={removeEmp} className="text-red-600" data-testid="delete-emp-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="perso">
        <TabsList>
          <TabsTrigger value="perso" data-testid="tab-perso"><UserCircle className="w-4 h-4 mr-1.5" />Personnel</TabsTrigger>
          <TabsTrigger value="pro" data-testid="tab-pro"><Briefcase className="w-4 h-4 mr-1.5" />Professionnel</TabsTrigger>
          <TabsTrigger value="contrats" data-testid="tab-contrats"><FileText className="w-4 h-4 mr-1.5" />Contrats ({contracts.length})</TabsTrigger>
          <TabsTrigger value="remuneration" data-testid="tab-remuneration"><DollarSign className="w-4 h-4 mr-1.5" />Rémunération</TabsTrigger>
          <TabsTrigger value="bulletins" data-testid="tab-bulletins"><ReceiptText className="w-4 h-4 mr-1.5" />Bulletins</TabsTrigger>
          <TabsTrigger value="absences" data-testid="tab-absences"><Calendar className="w-4 h-4 mr-1.5" />Absences</TabsTrigger>
          <TabsTrigger value="acomptes" data-testid="tab-acomptes"><Wallet className="w-4 h-4 mr-1.5" />Acomptes</TabsTrigger>
          <TabsTrigger value="saisies" data-testid="tab-saisies"><Gavel className="w-4 h-4 mr-1.5" />Saisies</TabsTrigger>
          <TabsTrigger value="mutuelle" data-testid="tab-mutuelle"><Heart className="w-4 h-4 mr-1.5" />Mutuelle</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><Folder className="w-4 h-4 mr-1.5" />Documents</TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical"><Stethoscope className="w-4 h-4 mr-1.5" />Médical</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training"><GraduationCap className="w-4 h-4 mr-1.5" />Formations</TabsTrigger>
          <TabsTrigger value="interview" data-testid="tab-interview"><MessageSquare className="w-4 h-4 mr-1.5" />Entretiens</TabsTrigger>
        </TabsList>

        <TabsContent value="perso" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Informations personnelles</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom</Label><Input value={form.first_name || ""} onChange={(e) => upd("first_name", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Nom</Label><Input value={form.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => upd("email", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Date de naissance</Label>
                  <DatePickerFR value={form.birth_date || ""} onChange={(v) => upd("birth_date", v)} disabled={!canWrite} />
                </div>
                <div><Label>Nationalité</Label><Input value={form.nationality || ""} onChange={(e) => upd("nationality", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>N° sécurité sociale</Label><Input value={form.ss_number || ""} onChange={(e) => upd("ss_number", e.target.value)} disabled={!canWrite} className="font-mono" /></div>
                <div><Label>Situation familiale</Label>
                  <Select value={form.family_status || ""} onValueChange={(v) => upd("family_status", v)} disabled={!canWrite}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Célibataire</SelectItem>
                      <SelectItem value="married">Marié(e)</SelectItem>
                      <SelectItem value="pacs">Pacsé(e)</SelectItem>
                      <SelectItem value="divorced">Divorcé(e)</SelectItem>
                      <SelectItem value="widowed">Veuf(ve)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-3 border-t">
                <Label className="text-xs uppercase text-slate-500">Adresse</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="col-span-2"><Label className="text-xs">Rue</Label>
                    <Input value={form.address?.street || ""} onChange={(e) => updNested("address", "street", e.target.value)} disabled={!canWrite} /></div>
                  <div><Label className="text-xs">CP</Label>
                    <Input value={form.address?.postal_code || ""} onChange={(e) => updNested("address", "postal_code", e.target.value)} disabled={!canWrite} /></div>
                  <div><Label className="text-xs">Ville</Label>
                    <Input value={form.address?.city || ""} onChange={(e) => updNested("address", "city", e.target.value)} disabled={!canWrite} /></div>
                </div>
              </div>
              <div className="pt-3 border-t">
                <Label className="text-xs uppercase text-slate-500">Coordonnées bancaires</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="col-span-2"><Label className="text-xs">IBAN</Label>
                    <Input value={form.bank_details?.iban || ""} onChange={(e) => updNested("bank_details", "iban", e.target.value)} disabled={!canWrite} className="font-mono" /></div>
                  <div><Label className="text-xs">BIC</Label>
                    <Input value={form.bank_details?.bic || ""} onChange={(e) => updNested("bank_details", "bic", e.target.value)} disabled={!canWrite} className="font-mono" /></div>
                  <div><Label className="text-xs">Banque</Label>
                    <Input value={form.bank_details?.bank_name || ""} onChange={(e) => updNested("bank_details", "bank_name", e.target.value)} disabled={!canWrite} /></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pro" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Informations professionnelles</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Poste</Label><Input value={form.job_title || ""} onChange={(e) => upd("job_title", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Service / Département</Label><Input value={form.department || ""} onChange={(e) => upd("department", e.target.value)} disabled={!canWrite} /></div>
                <div><Label>Catégorie</Label>
                  <Select value={form.category || ""} onValueChange={(v) => upd("category", v)} disabled={!canWrite}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cadre">Cadre</SelectItem>
                      <SelectItem value="non_cadre">Non-cadre</SelectItem>
                      <SelectItem value="etam">ETAM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Temps de travail</Label>
                  <Select value={form.work_time_type || "full_time"} onValueChange={(v) => upd("work_time_type", v)} disabled={!canWrite}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Temps plein</SelectItem>
                      <SelectItem value="part_time">Temps partiel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Heures/semaine</Label><Input type="number" step="0.5" value={form.hours_per_week || 35}
                  onChange={(e) => upd("hours_per_week", parseFloat(e.target.value))} disabled={!canWrite} /></div>
                <div><Label>Date d'embauche</Label>
                  <DatePickerFR value={form.hire_date || ""} onChange={(v) => upd("hire_date", v)} disabled={!canWrite} />
                </div>
                <div><Label>Statut</Label>
                  <Select value={form.status || "active"} onValueChange={(v) => upd("status", v)} disabled={!canWrite}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.status === "terminated" && (
                  <div><Label>Date de sortie</Label>
                    <DatePickerFR value={form.termination_date || ""} onChange={(v) => upd("termination_date", v)} disabled={!canWrite} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contrats" className="mt-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Contrats de travail</CardTitle>
              {canWrite && (
                <Button size="sm" onClick={addContract} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-contract-btn">
                  + Ajouter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Aucun contrat enregistré.</p>
              ) : (
                <div className="space-y-3">
                  {contracts.map((c, i) => (
                    <div key={c.id} className="p-3 border rounded-lg space-y-2" data-testid={`contract-item-${i}`}>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {CONTRACT_TYPES.find(t => t.value === c.contract_type)?.label || c.contract_type}
                        </Badge>
                        {canWrite && (
                          <Button variant="ghost" size="sm" onClick={() => removeContract(c.id)}
                            className="h-7 text-red-600" data-testid={`delete-contract-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div><Label className="text-xs">Type</Label>
                          <Select value={c.contract_type} onValueChange={(v) => updContract(c.id, { contract_type: v })} disabled={!canWrite}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{CONTRACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Début</Label>
                          <DatePickerFR value={c.start_date || ""} onChange={(v) => updContract(c.id, { start_date: v })} disabled={!canWrite} />
                        </div>
                        <div><Label className="text-xs">Fin (si CDD)</Label>
                          <DatePickerFR value={c.end_date || ""} onChange={(v) => updContract(c.id, { end_date: v })} disabled={!canWrite} />
                        </div>
                        <div><Label className="text-xs">Fin période d'essai</Label>
                          <DatePickerFR value={c.probation_end_date || ""} onChange={(v) => updContract(c.id, { probation_end_date: v })} disabled={!canWrite} />
                        </div>
                        <div><Label className="text-xs">Salaire brut annuel (€)</Label>
                          <Input type="number"
                            onBlur={(e) => updContract(c.id, { gross_annual_salary: parseFloat(e.target.value) || null })}
                            defaultValue={c.gross_annual_salary || ""} disabled={!canWrite} />
                        </div>
                        <div className="md:col-span-2"><Label className="text-xs">Convention collective</Label>
                          <Input defaultValue={c.collective_agreement || ""}
                            onBlur={(e) => updContract(c.id, { collective_agreement: e.target.value })} disabled={!canWrite} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="remuneration" className="mt-4">
          <RemunerationTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="bulletins" className="mt-4">
          <BulletinsTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="absences" className="mt-4">
          <AbsencesTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="acomptes" className="mt-4">
          <AcomptesTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="saisies" className="mt-4">
          <GarnishmentsTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="mutuelle" className="mt-4">
          <MutuelleTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <DocumentsTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="medical" className="mt-4">
          <MedicalTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="training" className="mt-4">
          <TrainingTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="interview" className="mt-4">
          <InterviewTab employeeId={id} canWrite={canWrite} />
        </TabsContent>
      </Tabs>

      <AIContractImportWizard
        open={aiEnrichOpen}
        onOpenChange={setAiEnrichOpen}
        mode="enrich"
        employeeId={id}
        onDone={() => load(false)}
      />
    </div>
  );
}
